import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, ExternalLink, Phone, Clock } from "lucide-react";

const STATUS_LABEL = {
  new: { en: "Awaiting Call", hi: "कॉल के इंतज़ार में", cls: "bg-amber-100 text-amber-800" },
  contacted: { en: "Contacted", hi: "संपर्क किया गया", cls: "bg-blue-100 text-blue-800" },
  converted: { en: "Deal Done", hi: "डील पूरी", cls: "bg-emerald-100 text-emerald-800" },
  closed: { en: "Closed", hi: "बंद", cls: "bg-slate-200 text-slate-700" },
};

function formatPrice(v) {
  if (v == null) return "-";
  const n = Number(v);
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function MyEnquiries() {
  const { user, loading: authLoading } = useAuth();
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/enquiries/mine")
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return <div className="py-20 text-center text-muted-foreground">{t.common.loading}</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/my-enquiries" }} replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <div className="overline text-secondary">Dashboard</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t.nav.myEnquiries}</h1>
        <p className="text-sm text-muted-foreground mt-2">
          {lang === "hi"
            ? "आपके द्वारा पूछी गई सभी प्रॉपर्टीज़ का स्टेटस यहाँ देखें।"
            : "Track the status of every property you've enquired about."}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">{t.common.loading}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl bg-accent/30" data-testid="my-enquiries-empty">
          <MessageCircle size={28} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-lg font-medium">
            {lang === "hi" ? "अभी तक कोई पूछताछ नहीं" : "No enquiries yet"}
          </p>
          <Link to="/browse" className="text-primary underline text-sm mt-1 inline-block">
            {lang === "hi" ? "प्रॉपर्टी देखें" : "Explore properties"}
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-enquiries-list">
          {items.map((e) => {
            const s = STATUS_LABEL[e.status] || STATUS_LABEL.new;
            return (
              <Card key={e.id} className="p-4 hover:border-primary/50 transition-colors" data-testid={`my-enq-${e.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/property/${e.property_id}`} className="font-semibold hover:text-primary flex items-center gap-1">
                        {e.property_title} <ExternalLink size={12} />
                      </Link>
                      <Badge className={`${s.cls} text-[10px] uppercase`}>{lang === "hi" ? s.hi : s.en}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {e.property_city} • {formatPrice(e.property_price)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Clock size={11} />
                        {new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {e.preferred_time && (
                        <span>🕒 {e.preferred_time}</span>
                      )}
                    </div>
                    {e.message && (
                      <p className="text-xs mt-2 italic text-muted-foreground">&ldquo;{e.message}&rdquo;</p>
                    )}
                    {e.admin_notes && (
                      <div className="mt-2 p-2 rounded-md bg-accent/60 text-xs">
                        <span className="font-semibold">BhoomiMitra: </span>{e.admin_notes}
                      </div>
                    )}
                  </div>
                  <a href="tel:+917014965172" className="shrink-0">
                    <div className="text-xs text-primary hover:underline flex items-center gap-1">
                      <Phone size={12} />+91 70149 65172
                    </div>
                  </a>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
